import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { CalendarIcon, Clock, AlertTriangle, Lock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import CleaningScope from "../../components/CleaningScope";
import SlotPresenceBadge from "../../components/SlotPresenceBadge";
import usePublicSettings from "../../lib/usePublicSettings";
import { Brand, brandShort } from "../../context/CompanyContext";

const EXTRAS_ICONS = {
  lavandaria: "🧺", consumiveis: "🧻", limpeza_profunda: "🧼", churrasqueira: "🔥", relva: "🌿", manutencao: "🔧", outros: "✨",
};

export default function NewBooking() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselectAptId = params.get("apartment_id");
  const { settings: publicSettings, loading: publicSettingsLoading } = usePublicSettings();
  const [types, setTypes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [form, setForm] = useState({
    apt_type: "T0",
    apartment_id: null,
    scheduled_date: "",
    scheduled_time: "",
    notes: "",
    extras: [],
    extras_notes: {},
    guest_name: "",
    guest_phone: "",
    entry_address: "",
    entry_instructions: "",
    address_street: "",
    address_number: "",
    address_postal_code: "",
    address_council: "",
  });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Dynamic slot map for the current (date, apt_type, apartment_id)
  // combination. Only `available` is rendered; `all_slots` is kept so we
  // can show booked ones disabled if needed.
  const [slots, setSlots] = useState({ available: [], booked: [], all_slots: [] });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // When client toggles "Outros", we prompt for a description. Pending state
  // holds the extra id while the modal is open; confirmed text is stored in
  // form.extras_notes so it's posted to the backend alongside the booking.
  const [descriptionFor, setDescriptionFor] = useState(null);
  const [descriptionText, setDescriptionText] = useState("");

  useEffect(() => {
    api.get("/catalog/apartment-types").then(({ data }) => setTypes(data.items));
    api.get("/catalog/extras").then(({ data }) => setExtras(data.items));
    api.get("/apartments").then(({ data }) => {
      setApartments(data.items);
      // Auto-select apartment if coming from the apartments page (deep-link)
      if (preselectAptId) {
        const a = data.items.find(x => x.id === preselectAptId);
        if (a) {
          setForm(f => ({
            ...f,
            apartment_id: a.id,
            apt_type: a.apt_type || f.apt_type,
            address_street: f.address_street || a.address || "",
          }));
        }
      }
    }).catch(() => {});
  }, [preselectAptId]);

  // Preview do preço só precisa de data + tipo + extras agora que a hora
  // já não é escolhida pelo cliente. O backend `/catalog/price-preview`
  // calcula só base + extras, ignora overlap quando a hora vem vazia.
  const canPreview = !!form.scheduled_date;

  // Slot fetching foi removido — cliente não escolhe hora; o admin define
  // a melhor hora depois de falar ao telefone.

  useEffect(() => {
    if (!canPreview) return;
    let cancelled = false;
    // Send the form WITHOUT scheduled_time so the backend doesn't validate
    // it. The pricing endpoint ignores time anyway when computing extras.
    const previewBody = { ...form, scheduled_time: "" };
    api.post("/catalog/price-preview", previewBody)
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.apt_type, form.scheduled_date, JSON.stringify(form.extras), canPreview]); // eslint-disable-line

  function toggleExtra(id) {
    const extra = extras.find(e => e.id === id);
    const already = form.extras.includes(id);
    // If this extra requires a description and the user is adding it, open
    // the dialog first — the extra is only added when they confirm.
    if (!already && extra?.requires_description) {
      setDescriptionText(form.extras_notes?.[id] || "");
      setDescriptionFor(id);
      return;
    }
    setForm(f => {
      const newExtras = already ? f.extras.filter(x => x !== id) : [...f.extras, id];
      const newNotes = { ...(f.extras_notes || {}) };
      if (already) delete newNotes[id]; // remove its note when deselected
      return { ...f, extras: newExtras, extras_notes: newNotes };
    });
  }

  function confirmDescription() {
    const txt = descriptionText.trim();
    if (txt.length < 5) {
      toast.error("Descreva o serviço pretendido (mínimo 5 caracteres).");
      return;
    }
    const id = descriptionFor;
    setForm(f => ({
      ...f,
      extras: f.extras.includes(id) ? f.extras : [...f.extras, id],
      extras_notes: { ...(f.extras_notes || {}), [id]: txt },
    }));
    setDescriptionFor(null);
    setDescriptionText("");
  }

  function cancelDescription() {
    setDescriptionFor(null);
    setDescriptionText("");
  }

  function pickDate(d) {
    if (!d) return;
    setSelectedDate(d);
    const iso = format(d, "yyyy-MM-dd");
    setForm(f => ({ ...f, scheduled_date: iso }));
    setCalendarOpen(false);
  }

  /**
   * Returns the *first* reason the booking cannot be submitted yet, or
   * an empty string when everything is OK. Used for the inline helper
   * text right above the submit button so the user always sees WHY the
   * button isn't progressing instead of staring at a silent disabled
   * gold pill.
   */
  function missingReason() {
    if (!form.apt_type) return { msg: "Escolha o tipo de alojamento.", focus: null };
    if (!form.scheduled_date) return { msg: "Escolha a data da limpeza.", focus: "booking-date" };
    if (!String(form.guest_name || "").trim()) return { msg: "Indique o nome da pessoa que recebe a equipa.", focus: "booking-guest-name" };
    if (!String(form.guest_phone || "").trim()) return { msg: "Indique o telemóvel de contacto.", focus: "booking-guest-phone" };
    if (!String(form.address_street || "").trim()) return { msg: "Indique a rua do alojamento.", focus: "booking-address-street" };
    if (!String(form.address_number || "").trim()) return { msg: "Indique o número (porta · andar · fração).", focus: "booking-address-number" };
    if (!String(form.address_postal_code || "").trim()) return { msg: "Indique o código postal.", focus: "booking-address-postal" };
    if (!String(form.address_council || "").trim()) return { msg: "Escolha o concelho.", focus: "booking-address-council" };
    if (!String(form.entry_instructions || "").trim()) return { msg: "Indique como entramos (chave, código, lockbox…).", focus: "booking-entry-instructions" };
    return { msg: "", focus: null };
  }
  const blocked = missingReason();
  const blockedReason = blocked.msg;

  async function submit(e) {
    e.preventDefault();
    if (blockedReason) {
      toast.error(blockedReason);
      if (blocked.focus) {
        const el = document.querySelector(`[data-testid="${blocked.focus}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof el.focus === "function") setTimeout(() => el.focus(), 350);
        }
      }
      return;
    }
    setBusy(true);
    try {
      // Strip scheduled_time before submitting — clients never pick a
      // time. Backend treats null as "to be confirmed by LuxStay".
      const payload = { ...form, scheduled_time: null };
      const { data } = await api.post("/bookings", payload);
      toast.success(`Reserva criada! Por favor entre em contacto com a ${brandShort()} para confirmar.`);
      nav(`/cliente/reservas/${data.id}`, { state: { justCreated: true } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason === "missing_required_fields") {
        const labels = {
          guest_name: "Nome da pessoa",
          guest_phone: "Telemóvel",
          entry_address: "Morada (rua · número · cód. postal · concelho)",
          entry_instructions: "Instruções para entrada",
        };
        const items = (detail.missing || []).map(k => labels[k] || k).join(", ");
        toast.error(`Preencha: ${items}`);
      } else if (detail && typeof detail === "object" &&
                 (detail.reason === "apartment_overlap" || detail.reason === "no_capacity")) {
        // Friendly conflict feedback. Suggests the next free slot and
        // refreshes the in-page slot picker so the user can simply tap
        // a different hour. Pre-selects the suggested slot if any.
        const conflict = detail.conflict;
        const next = detail.next_available_after;
        const alts = (detail.alternatives || []).slice(0, 3).join(", ");
        const conflictMsg = conflict
          ? `O horário escolhido já tem uma limpeza ${conflict.apt_type} marcada às ${conflict.time} (duração ${conflict.duration} min + buffer ${detail.buffer_minutes ?? 60} min).`
          : "Esse horário já está reservado.";
        toast.error(
          `${conflictMsg}${next ? ` Próximo livre: ${next}.` : ""}${alts ? ` Sugestões: ${alts}.` : ""}`,
          { duration: 8000 },
        );
        // Refresh slots so the picker reflects the current state
        setForm(f => ({
          ...f,
          scheduled_time: next && (typeof next === "string") ? next : "",
        }));
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  }

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  // Quando o admin desliga as reservas, mostramos um banner em vez do
  // formulário. O backend também rejeita POST /bookings para clientes.
  if (!publicSettingsLoading && publicSettings.bookings_enabled === false) {
    return (
      <div className="max-w-3xl space-y-8" data-testid="bookings-closed">
        <div>
          <div className="eyebrow mb-3">Novo agendamento</div>
          <h1 className="font-display text-5xl">Reservas em pausa.</h1>
        </div>
        <div className="lux-card p-8 border-2 border-[#dc2626] bg-[#dc2626]/10 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#dc2626] grid place-items-center">
              <Lock size={22} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#fca5a5] font-semibold">
                Reservas online temporariamente fechadas
              </div>
              <div className="font-display text-2xl mt-1">Falamos consigo diretamente</div>
            </div>
          </div>
          <p className="text-sm text-[#fdfbf7] leading-relaxed">
            {publicSettings.bookings_closed_message
              || "As reservas online estão temporariamente fechadas. Por favor contacte-nos diretamente para marcar a sua limpeza."}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href="https://wa.me/351967386076"
               target="_blank" rel="noreferrer"
               className="p-4 bg-[#062c27] border border-[#1a4640] hover:border-[#25d366] hover:bg-[#25d366]/10 transition-colors"
               data-testid="closed-whatsapp">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">WhatsApp</div>
              <div className="text-base font-semibold mt-1">+351 967 386 076</div>
            </a>
            <a href="mailto:luxstaymadeira@gmail.com"
               className="p-4 bg-[#062c27] border border-[#1a4640] hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
               data-testid="closed-email">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">E-mail</div>
              <div className="text-base font-semibold mt-1">luxstaymadeira@gmail.com</div>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-10">
      <div>
        <div className="eyebrow mb-3">Novo agendamento</div>
        <h1 className="font-display text-5xl">Reserve em segundos.</h1>
      </div>

      <form onSubmit={submit} noValidate className="grid lg:grid-cols-3 gap-8" data-testid="booking-form">
        <div className="lg:col-span-2 space-y-6">
          <div className="lux-card p-6">
            <div className="eyebrow mb-4">Tipo de apartamento</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {types.map(t => (
                <button type="button" key={t.type} onClick={() => setForm(f => ({ ...f, apt_type: t.type }))}
                        className={`p-4 min-w-0 border rounded-sm text-left transition-colors ${form.apt_type === t.type ? "border-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640] hover:border-[#d4af37]/60"}`}
                        data-testid={`type-${t.type}`}>
                  <div className="font-display text-2xl break-words leading-tight" style={{ overflowWrap: "anywhere" }}>{t.type}</div>
                  <div className="text-[11px] text-[#a0b2af] mt-0.5 truncate">{t.label || t.type}</div>
                  <div className="text-xs text-[#a0b2af] mt-2">
                    {Math.floor(t.duration / 60)}h{t.duration % 60 ? ` ${t.duration % 60}m` : ""}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 p-3 border border-[#d4af37]/50 bg-[#d4af37]/10 rounded-sm"
                 data-testid="custom-quote-notice">
              <AlertTriangle size={16} className="text-[#d4af37] shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed text-[#fdfbf7]">
                O <strong>valor final</strong> da sua reserva é combinado por contacto. A <Brand /> liga-lhe
                para confirmar a tipologia, o estado do alojamento e quaisquer extras antes de fechar o preço.
                Pode também ligar para <strong>+351 967 386 076</strong> ou escrever para <strong>luxstaymadeira@gmail.com</strong>.
              </p>
            </div>
          </div>

          {apartments.length > 0 && (
            <div className="lux-card p-6">
              <div className="eyebrow mb-4">Apartamento (opcional)</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setForm(f => ({ ...f, apartment_id: null }))}
                        className={`px-3 py-2 text-sm border rounded-sm ${!form.apartment_id ? "border-[#d4af37] text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af]"}`}
                        data-testid="apt-none">
                  Sem seleção
                </button>
                {apartments.map(a => (
                  <button type="button" key={a.id}
                          onClick={() => setForm(f => ({
                            ...f,
                            apartment_id: a.id,
                            apt_type: a.apt_type,
                            // Auto-preencher morada a partir do apartamento se vazia
                            address_street: f.address_street || a.address || "",
                          }))}
                          className={`px-3 py-2 text-sm border rounded-sm ${form.apartment_id === a.id ? "border-[#d4af37] text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af]"}`}
                          data-testid={`apt-${a.id}`}>
                    {a.name} · {a.apt_type}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="lux-card p-6 grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Data</label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button type="button"
                    className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-left flex items-center justify-between hover:border-[#d4af37]"
                    data-testid="booking-date">
                    <span className={selectedDate ? "" : "text-[#a0b2af]"}>
                      {selectedDate ? format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: pt }) : "Escolher data"}
                    </span>
                    <CalendarIcon size={16} className="text-[#d4af37]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-[#062c27] border-[#1a4640]" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={pickDate}
                    disabled={{ before: today }}
                    locale={pt}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2" data-testid="client-no-time-info">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">
                Hora
              </label>
              <div className="w-full bg-[#062c27] border border-[#1a4640] border-dashed rounded-sm px-4 py-3 flex items-center gap-3">
                <Clock size={16} className="text-[#d4af37]" />
                <div className="text-sm text-[#fdfbf7] leading-snug">
                  A hora é <strong className="text-[#d4af37]">combinada por telefone</strong>.
                  <div className="text-[11px] text-[#a0b2af] mt-0.5">
                    A <Brand short /> liga-lhe para confirmar a melhor hora dentro do dia escolhido.
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-[#a0b2af]">
                Horário de trabalho: 09:00 – 17:00 · contacto após o pedido
              </div>
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Notas (opcional)</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        rows={2} placeholder="Qualquer informação adicional…"
                        className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                        data-testid="booking-notes" />
            </div>
          </div>

          {/* Dados obrigatórios para a equipa de limpeza */}
          <div className="lux-card p-6 space-y-4" data-testid="mandatory-fields">
            <div>
              <div className="eyebrow">Dados do hóspede & acesso</div>
              <div className="text-[11px] text-[#a0b2af] mt-1">Obrigatórios · a equipa de limpeza precisa destas informações para executar o serviço</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Nome da pessoa *</label>
                <input type="text" required minLength={2}
                       value={form.guest_name}
                       onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                       placeholder="Nome do hóspede ou responsável"
                       className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                       data-testid="booking-guest-name" />
              </div>
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Telemóvel *</label>
                <input type="tel" required minLength={6}
                       value={form.guest_phone}
                       onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value.replace(/[^\d\s+]/g, "") }))}
                       placeholder="912 345 678"
                       inputMode="tel"
                       className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 font-mono"
                       data-testid="booking-guest-phone" />
                <p className="text-[11px] text-[#a0b2af]">Não precisa de +351 — adicionamos automaticamente.</p>
              </div>
              <div className="md:col-span-2 grid md:grid-cols-3 gap-3" data-testid="booking-address-fields">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Rua *</label>
                  <input type="text" required minLength={2}
                         value={form.address_street}
                         onChange={e => setForm(f => ({ ...f, address_street: e.target.value }))}
                         placeholder="Ex: Rua do Conselheiro Aires de Ornelas"
                         className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                         data-testid="booking-address-street" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Número *</label>
                  <input type="text" required
                         value={form.address_number}
                         onChange={e => setForm(f => ({ ...f, address_number: e.target.value }))}
                         placeholder="Nº · andar · fração"
                         className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                         data-testid="booking-address-number" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Código postal *</label>
                  <input type="text" required
                         value={form.address_postal_code}
                         onChange={e => setForm(f => ({ ...f, address_postal_code: e.target.value }))}
                         placeholder="9000-000"
                         className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 font-mono"
                         data-testid="booking-address-postal" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Concelho *</label>
                  <select required
                          value={form.address_council}
                          onChange={e => setForm(f => ({ ...f, address_council: e.target.value }))}
                          className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                          data-testid="booking-address-council">
                    <option value="">— Escolha o concelho —</option>
                    <option value="Funchal">Funchal</option>
                    <option value="Câmara de Lobos">Câmara de Lobos</option>
                    <option value="Santa Cruz">Santa Cruz</option>
                    <option value="Machico">Machico</option>
                    <option value="Ribeira Brava">Ribeira Brava</option>
                    <option value="Ponta do Sol">Ponta do Sol</option>
                    <option value="Calheta">Calheta</option>
                    <option value="Santana">Santana</option>
                    <option value="São Vicente">São Vicente</option>
                    <option value="Porto Moniz">Porto Moniz</option>
                    <option value="Porto Santo">Porto Santo</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Instruções para entrada *</label>
                <textarea required minLength={3} rows={3}
                          value={form.entry_instructions}
                          onChange={e => setForm(f => ({ ...f, entry_instructions: e.target.value }))}
                          placeholder="Ex: Código da porta 2580 · Chaves no cofre (1234) · Receção chama ao T2 · Código portão garagem"
                          className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                          data-testid="booking-entry-instructions" />
              </div>
            </div>
          </div>

          <div className="lux-card p-6">
            <div className="eyebrow mb-4">Extras automáticos</div>
            <div className="grid md:grid-cols-2 gap-3">
              {extras.map(ex => {
                const selected = form.extras.includes(ex.id);
                const note = form.extras_notes?.[ex.id];
                return (
                  <div key={ex.id} className="space-y-1.5">
                    <button type="button" onClick={() => toggleExtra(ex.id)}
                            className={`w-full p-4 border flex items-center justify-between ${selected ? "border-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640]"}`}
                            data-testid={`extra-${ex.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{EXTRAS_ICONS[ex.id] || "✦"}</span>
                        <span className="text-sm text-left">{ex.name}</span>
                      </div>
                      <span className="text-[#d4af37] text-sm whitespace-nowrap">
                        A combinar
                      </span>
                    </button>
                    {selected && note && (
                      <div className="text-[11px] text-[#a0b2af] px-1 flex items-start justify-between gap-2"
                           data-testid={`extra-note-${ex.id}`}>
                        <span className="italic">"{note}"</span>
                        <button type="button"
                                onClick={() => { setDescriptionText(note); setDescriptionFor(ex.id); }}
                                className="text-[#d4af37] hover:underline shrink-0"
                                data-testid={`extra-note-edit-${ex.id}`}>
                          Editar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="lux-card p-6 h-fit sticky top-6" data-testid="booking-summary">
          <div className="eyebrow mb-4">Resumo do pedido</div>

          <div className="space-y-3 text-sm">
            <Row k="Tipologia" v={form.apt_type || "—"} />
            <Row k="Data" v={form.scheduled_date || "—"} />
            <Row k="Extras" v={form.extras.length > 0 ? `${form.extras.length} selecionados` : "Nenhum"} />
            <CleaningScope testId="booking-scope" />
          </div>

          <div className="mt-5 p-4 border-2 border-[#d4af37] bg-[#d4af37]/10 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37] font-semibold mb-2">
              Valor final · combinado por contacto
            </div>
            <p className="text-xs leading-relaxed text-[#fdfbf7]">
              O valor da sua reserva é confirmado por <strong>telefone</strong> ou{" "}
              <strong>WhatsApp</strong> depois de validarmos a tipologia, o estado do alojamento
              e os extras pedidos. Receberá um e-mail com os contactos assim que submeter o pedido.
            </p>
            <div className="mt-3 text-[11px] text-[#a0b2af] space-y-1">
              <div><strong className="text-[#fdfbf7]">+351 967 386 076</strong> · WhatsApp / chamada</div>
              <div><strong className="text-[#fdfbf7]">luxstaymadeira@gmail.com</strong></div>
            </div>
          </div>
          {blockedReason && (
            <div className="mt-4 p-3 rounded-sm bg-[#f59e0b]/10 border border-[#f59e0b]/40 text-xs text-[#fcd34d] flex items-start gap-2"
                 data-testid="booking-blocked-reason">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{blockedReason}</span>
            </div>
          )}
          <button disabled={busy} className="btn-gold w-full py-3 mt-4 disabled:opacity-50" data-testid="booking-submit">
            {busy ? "A enviar…" : "Confirmar agendamento"}
          </button>
        </aside>
      </form>

      {descriptionFor && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60" onClick={cancelDescription} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="lux-card bg-[#041f1b] border border-[#1a4640] w-full max-w-md pointer-events-auto"
                 data-testid="extra-description-modal">
              <div className="px-5 py-4 border-b border-[#1a4640]">
                <div className="eyebrow text-[#d4af37]">
                  {extras.find(e => e.id === descriptionFor)?.name || "Descreva o serviço"}
                </div>
                <div className="text-xs text-[#a0b2af] mt-1">
                  Explique o que precisa para o admin preparar corretamente. O valor será combinado depois.
                </div>
              </div>
              <div className="p-5 space-y-3">
                <textarea
                  value={descriptionText}
                  onChange={(e) => setDescriptionText(e.target.value)}
                  rows={5}
                  placeholder="Ex.: Entrega de kit de boas-vindas com garrafa de vinho da Madeira e pão-de-ló no dia de entrada dos hóspedes."
                  maxLength={1000}
                  autoFocus
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none resize-none"
                  data-testid="extra-description-input"
                />
                <div className="text-[10px] text-[#a0b2af] text-right">
                  {descriptionText.length}/1000
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#1a4640]">
                <button type="button" onClick={cancelDescription}
                        className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                        data-testid="extra-description-cancel">
                  Cancelar
                </button>
                <button type="button" onClick={confirmDescription}
                        className="btn-gold text-xs px-5 py-2.5 uppercase tracking-widest"
                        data-testid="extra-description-confirm">
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-[#a0b2af]">{k}</div>
      <div>{v}</div>
    </div>
  );
}
